/**
 * Состояние фильтров на клиенте.
 *
 * Зеркало серверного контракта. Правит его и человек руками, и чат репликой —
 * это одно и то же действие над одной структурой, поэтому отдельного
 * «состояния чата» здесь нет.
 *
 * Период хранится символически, как на сервере: границы считает Python, и
 * подмена спецификации парой дат потеряла бы подпись «за 2 квартал 2026» и
 * сломала следующую реплику вида «а в феврале?».
 */

import type { FilterState, Granularity, MetricKey, PeriodSpec, RoleKey } from "../api/types";

export const DEFAULT_STATE: FilterState = {
  period: { kind: "all" },
  person: null,
  person_b: null,
  program: null,
  role: "host",
  metric: "views",
  limit: null,
  granularity: null,
};

export const METRIC_LABELS: Record<MetricKey, string> = {
  views: "просмотры",
  minutes: "минуты просмотра",
  subscribers: "подписки",
  revenue: "доход",
  episodes: "выпуски",
};

export const ROLE_LABELS: Record<RoleKey, string> = {
  host: "ведущий",
  guest: "гость",
  author: "автор",
  any: "любая роль",
};

export const GRANULARITY_LABELS: Record<Granularity, string> = {
  day: "по дням",
  week: "по неделям",
  month: "по месяцам",
};

const MONTHS = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

/** Подпись периода до ответа сервера — для мгновенной отрисовки чипа. */
export function periodLabel(period: PeriodSpec): string {
  switch (period.kind) {
    case "year":
      return `${period.year} год`;
    case "quarter":
      return `${period.index} квартал ${period.year}`;
    case "month":
      return `${MONTHS[(period.index ?? 1) - 1] ?? period.index} ${period.year}`;
    case "range":
      return `${period.date_from} — ${period.date_to}`;
    case "last_days":
      return `последние ${period.days} дней`;
    default:
      return "за всё время";
  }
}

// --------------------------------------------------------------- адрес строки

/**
 * Состояние в адресной строке: ссылку на вид можно переслать коллеге.
 * Форма читаемая намеренно — по адресу видно, что на экране.
 */
export function stateToQuery(state: FilterState): string {
  const params = new URLSearchParams();
  params.set("period", encodePeriod(state.period));
  if (state.person) params.set("person", state.person);
  if (state.person_b) params.set("person_b", state.person_b);
  if (state.program) params.set("program", state.program);
  if (state.role !== DEFAULT_STATE.role) params.set("role", state.role);
  if (state.metric !== DEFAULT_STATE.metric) params.set("metric", state.metric);
  if (state.limit) params.set("limit", String(state.limit));
  if (state.granularity) params.set("granularity", state.granularity);
  return params.toString();
}

export function stateFromQuery(search: string): FilterState {
  const params = new URLSearchParams(search);
  const role = params.get("role");
  const metric = params.get("metric");
  const granularity = params.get("granularity");
  const limit = Number(params.get("limit"));
  return {
    period: decodePeriod(params.get("period")),
    person: params.get("person"),
    person_b: params.get("person_b"),
    program: params.get("program"),
    role: isRole(role) ? role : DEFAULT_STATE.role,
    metric: isMetric(metric) ? metric : DEFAULT_STATE.metric,
    limit: Number.isFinite(limit) && limit > 0 ? limit : null,
    granularity: isGranularity(granularity) ? granularity : null,
  };
}

function encodePeriod(period: PeriodSpec): string {
  switch (period.kind) {
    case "year":
      return `year:${period.year}`;
    case "quarter":
      return `quarter:${period.year}:${period.index}`;
    case "month":
      return `month:${period.year}:${period.index}`;
    case "range":
      return `range:${period.date_from}:${period.date_to}`;
    case "last_days":
      return `last_days:${period.days}`;
    default:
      return "all";
  }
}

/** Числа из адреса приходят от кого угодно, поэтому загоняются в рамки. */
const bounded = (raw: string | undefined, min: number, max: number, fallback: number) => {
  const value = Number(raw);
  return Number.isFinite(value) && value >= min && value <= max ? Math.trunc(value) : fallback;
};

function decodePeriod(raw: string | null): PeriodSpec {
  if (!raw) return { kind: "all" };
  const [kind, a, b] = raw.split(":");
  const year = bounded(a, 2000, 2100, new Date().getFullYear());
  switch (kind) {
    case "year":
      return { kind: "year", year };
    case "quarter":
      return { kind: "quarter", year, index: bounded(b, 1, 4, 1) };
    case "month":
      return { kind: "month", year, index: bounded(b, 1, 12, 1) };
    case "range":
      return { kind: "range", date_from: a, date_to: b };
    case "last_days":
      return { kind: "last_days", days: bounded(a, 1, 3650, 30) };
    default:
      return { kind: "all" };
  }
}

const isRole = (value: string | null): value is RoleKey =>
  value === "host" || value === "guest" || value === "author" || value === "any";

const isMetric = (value: string | null): value is MetricKey =>
  value === "views" ||
  value === "minutes" ||
  value === "subscribers" ||
  value === "revenue" ||
  value === "episodes";

const isGranularity = (value: string | null): value is Granularity =>
  value === "day" || value === "week" || value === "month";
